import { styled } from "@linaria/react";

import { withTheme } from "../theme/GalaxyTheme";
import type { PropsWithTheme } from "../theme/types";

import GalaxyLogomark from "../icons/GalaxyLogomark";
import AvatarEmpty from "./AvatarEmpty";
import AvatarShimmer from "./AvatarShimmer";

export interface AvatarProps {
  img?: string | null;
  isLoading?: boolean;
  size?: number;
  hex?: string;
  shouldShowLogo?: boolean;
}

const StyledAvatar = styled.img<{
  $size: number;
}>`
  border-radius: 200px;
  height: ${({ $size }) => $size}px;
  width: ${({ $size }) => $size}px;
`;

const StyledLogoAvatar = withTheme(styled.div<
  PropsWithTheme<{
    $size: number;
  }>
>`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  background-color: ${({ theme }) => theme.color.background.tertiary};
  border: 0.5px solid ${({ theme }) => theme.color.border.primary};
  padding: ${({ $size }) => {
    // Calculate padding based on size: smaller sizes get less padding proportionally
    if ($size <= 16) return "2px";
    if ($size <= 24) return "3px";
    return "4px";
  }};
  height: ${({ $size }) => $size}px;
  width: ${({ $size }) => $size}px;
`);

const Avatar = ({ img, isLoading, size = 16, hex, shouldShowLogo }: AvatarProps) => {
  if (isLoading) {
    return isLoading ? <AvatarShimmer size={size} /> : null;
  }

  if (shouldShowLogo) {
    const padding = size <= 16 ? 2 : size <= 24 ? 3 : 4;
    const logoSize = Math.max(8, size - padding * 2 - 2); // Subtract extra 2px to make it smaller

    return (
      <StyledLogoAvatar $size={size}>
        <GalaxyLogomark height={logoSize} />
      </StyledLogoAvatar>
    );
  }

  if (!img) {
    return <AvatarEmpty size={size} hex={hex} />;
  }

  return <StyledAvatar src={img} alt="Avatar" $size={size} />;
};

export default Avatar;
